import { flightAutoComplete } from '@/actions/flights/autoComplete';
import ModalPage from '@/components/ui/Modal';
import PlaneIcon from '@/components/icons/Flights/PlaneIcon';
import LocationIcon from '@/components/icons/Location';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import useSetSearchParams from '@/hooks/useSetSearchParams';
import { deslugify } from '@/utils/slugify';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/AccordionCn';

import Image from 'next/image';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Modal from '@/components/ui/Modal';
import useSizeController from '@/hooks/useSizeController';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';

// Define the types for the prediction data
interface Airport {
    airport_name: string;
    airport_code: string;
    airport_type: string;
}

interface Prediction {
    city: string;
    country_name: string;
    airports: Airport[];
    id?: number; // Added id as it's added in the get function
}

interface ResultRendererProps {
    isSmall?: boolean
    predictions: Prediction[];
    setShowCities: Dispatch<SetStateAction<boolean>>;
    setCityError: Dispatch<SetStateAction<boolean>>;
    setSelectedAirport: Dispatch<SetStateAction<{
        airport_name: string;
        airport_code: string;
    }>>;
    setCity: Dispatch<SetStateAction<string>>;
}

export default function FlightSearchInput({ hasError, setSelectedAirport, selectedAirport, onFocus }: {
    hasError?: boolean,
    onFocus?: () => void;
    setSelectedAirport: Dispatch<SetStateAction<{
        airport_name: string;
        airport_code: string;
    }>>,
    selectedAirport: {
        airport_name: string;
        airport_code: string;
    }
}) {

    const [cityError, setCityError] = useState(false);
    const [isGettingCities, setIsGettingCities] = useState(false);
    const [city, setCity] = useState(selectedAirport.airport_name);
    const [showCities, setShowCities] = useState(false);
    const [fetchError, setFetchError] = useState("");

    const [predictions, setPredictions] = useState<Prediction[]>([]);

    useEffect(() => {
        setCity(selectedAirport.airport_name)
    }, [selectedAirport.airport_code])


    const { params, mainParams } = useSetSearchParams();
    const { country: countryParams } = mainParams;
    const country = countryParams?.[0]
    const cityParam = countryParams?.[1]
    const cityModal = useOutsideClick(() => {
        // Only hide if city is not selected and input is not empty
        if (selectedAirport.airport_code === "" && city.length > 0) {
            // Optionally set city error here if needed
        }
        !isSmall && setShowCities(false);
    });

    const get = async (city: string) => {
        setIsGettingCities(true);
        try {

            const res = await flightAutoComplete(city);

            const { result } = await JSON.parse(res)
            console.log({ result });

            const cityNames = result.data?.map(
                (prediction: Prediction, index: number) => { // Use Prediction type here
                    return {
                        ...prediction,
                        id: index,
                    };
                },
            );
            setIsGettingCities(false);
            setPredictions(cityNames || []);
        } catch (err) {
            setIsGettingCities(false);
            setFetchError("Something went wrong!");
        }
    };

    useEffect(() => {
        setFetchError("");

        if (city && city.length > 0 && showCities) {
            setIsGettingCities(true);
            const getData = setTimeout(() => {
                get(city);
            }, 400);
            return () => clearTimeout(getData);
        } else {

            setPredictions([]); // Clear predictions if input matches selected or is empty
            setIsGettingCities(false);
        }


    }, [city, selectedAirport.airport_name, showCities]); // Added selectedAirport.airport_name to dependencies

    useEffect(() => {
        if (cityParam || country) {
            // Ensure this logic correctly sets the display city but doesn't trigger a new search unless needed
            const displayCity = `${deslugify(country)} ${cityParam ? `, ${deslugify(cityParam)}` : ''}`;
            if (city !== displayCity) { // Prevent unnecessary state update and potential fetch
                setCity(displayCity as string);
            }
        } else {
        }
    }, [cityParam, country, city]);

    const { isSmall } = useSizeController()

    return (
        <>
            <div ref={cityModal}>
                <input
                    placeholder={'Enter your destination'}
                    value={
                        city
                    }
                    className={`${hasError || cityError ? "border-rose-500" : "border"}
                    peer capitalize relative border-0 h-full w-full pl-0 pr-2.5 py-1 text-neutral-800 font-normal
                    placeholder:text-neutral-500 placeholder:font-normal placeholder:text-sm
                 `}
                    onChange={async (e) => {
                        setShowCities(true)
                        setCity(e.target.value);
                        // Clear selected airport when the user starts typing a new city
                        setSelectedAirport({
                            airport_name: "",
                            airport_code: "",
                        });
                        setCityError(false); // Clear city error on input change
                    }}
                    onClick={() => {
                        setShowCities(true)
                    }}
                    onFocus={() => {
                        onFocus && onFocus()
                        setShowCities(true);
                    }}
                    type="text"
                />

                {cityError && (
                    <p className="ps-1 pt-1 text-sm font-normal text-red-600">
                        Please choose your destination.
                    </p>
                )}

                {
                    isGettingCities && city.length > 0 && selectedAirport.airport_code === "" ?
                        ( // Only show loading if input is not a selected airport
                            <div
                                className="absolute bottom-0 z-50  flex h-0 w-full min-w-fit
                    translate-y-[96%] overflow-hidden rounded-lg border bg-white p-3 opacity-0 shadow transition-all
                     duration-300 peer-focus:h-fit peer-focus:translate-y-full peer-focus:opacity-100"
                            >
                                <div className="flex w-full flex-col gap-8 pt-1">
                                    <div className="flex w-full items-center gap-3">
                                        <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                        <div className="flex flex-col gap-2">
                                            <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                            <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                        </div>
                                    </div>
                                    <div className="flex w-full items-center gap-3">
                                        <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                        <div className="flex flex-col gap-2">
                                            <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                            <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                        </div>
                                    </div>
                                    <div className="flex w-full items-center gap-3">
                                        <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                        <div className="flex flex-col gap-2">
                                            <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                            <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                        </div>
                                    </div>
                                    <div className="flex w-full items-center gap-3">
                                        <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                        <div className="flex flex-col gap-2">
                                            <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                            <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                        </div>
                                    </div>
                                    <div className="flex w-full items-center gap-3">
                                        <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                        <div className="flex flex-col gap-2">
                                            <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                            <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) :
                        !isSmall && predictions.length > 0 && city.length > 0 && selectedAirport.airport_code === "" ? ( // Only show results if input is not a selected airport
                            <div
                                className={`${showCities
                                    ? "pointer-events-auto translate-y-full opacity-100"
                                    : "pointer-events-none translate-y-[96%] opacity-0"
                                    }
                     absolute left-0 bottom-0 z-50  flex text-base font-normal h-fit w-full min-w-[384px] flex-col
                     items-stretch  overflow-hidden rounded-lg border bg-white px-2
                      py-2 shadow transition-all duration-300
                     `}
                            >
                                <ResultRenderer
                                    predictions={predictions}
                                    setShowCities={setShowCities}
                                    setCityError={setCityError}
                                    setSelectedAirport={setSelectedAirport}
                                    setCity={setCity}
                                />

                            </div>
                        ) : (
                            city.length > 0 && selectedAirport.airport_code === "" && ( // Only show not found if input is not a selected airport
                                <div
                                    className="absolute bottom-0 z-50 hidden h-0 w-full min-w-fit translate-y-[96%]
                         overflow-hidden rounded-lg border bg-white p-3 opacity-0 shadow transition-all duration-300
                          peer-focus:h-fit peer-focus:translate-y-full peer-focus:opacity-100 lg:flex"
                                >
                                    <div
                                        className="group flex w-full max-w-full cursor-pointer items-center gap-3
                            transition-colors duration-200 hover:text-emerald-700 "
                                    >
                                        {fetchError ? (
                                            <div className="flex w-full flex-col items-center justify-center gap-4 ">
                                                <p className="text-orange-500">{fetchError}</p>

                                                <Image
                                                    className="w-32"
                                                    src={"https://assets.bamboo.travel/wrong.svg"}
                                                    width={300}
                                                    height={300}
                                                    alt="Nothing Found"
                                                />
                                            </div>
                                        ) : (
                                            city.length > 0 && (
                                                <div className="flex w-full flex-col items-center justify-center gap-4 ">
                                                    <p className='text-neutral-700 text-sm'>nothing was found!</p>

                                                    <Image
                                                        className="w-16"
                                                        src={"https://assets.bamboo.travel/looking.svg"}
                                                        width={300}
                                                        height={300}
                                                        alt="Nothing Found"
                                                    />
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            )
                        )}
            </div>
            <Modal
                bgColor='bg-white'
                isFull
                show={isSmall && showCities}
                onClose={() => setShowCities(false)} identifier='Airports'>
                <div>
                    <div className='pb-4 border-b  grid grid-cols-[16px,_1fr] gap-4'>
                        <button onClick={() => setShowCities(false)} className=''>
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <div>
                            <input
                                placeholder={'Enter your destination'}
                                value={
                                    city
                                }
                                className={`${hasError || cityError ? "border-rose-500" : "border"}
                    peer relative capitalize border rounded-lg h-full w-full text-neutral-800 font-normal
                    placeholder:text-neutral-500 placeholder:font-normal placeholder:text-sm
                    p-2 
                 `}
                                onChange={async (e) => {
                                    setShowCities(true)
                                    setCity(e.target.value);
                                    // Clear selected airport when the user starts typing a new city
                                    setSelectedAirport({
                                        airport_name: "",
                                        airport_code: "",
                                    });
                                    setCityError(false); // Clear city error on input change
                                }}
                                onClick={() => {
                                    setShowCities(true)
                                }}
                                onFocus={() => {
                                    onFocus && onFocus()
                                    setShowCities(true);
                                }}
                                type="text"
                            />
                        </div>
                    </div>
                    {predictions.length > 0 && city.length > 0 && !isGettingCities && (
                        <ResultRenderer
                            isSmall
                            predictions={predictions}
                            setShowCities={setShowCities}
                            setCityError={setCityError}
                            setSelectedAirport={setSelectedAirport}
                            setCity={setCity}
                        />
                    )}
                    {isGettingCities && city.length > 0 && <div className='py-4 px-2'>

                        <div className="flex w-full flex-col gap-8 pt-1">
                            <div className="flex w-full items-center gap-3">
                                <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                <div className="flex flex-col gap-2">
                                    <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                    <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                </div>
                            </div>
                            <div className="flex w-full items-center gap-3">
                                <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                <div className="flex flex-col gap-2">
                                    <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                    <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                </div>
                            </div>
                            <div className="flex w-full items-center gap-3">
                                <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                <div className="flex flex-col gap-2">
                                    <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                    <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                </div>
                            </div>
                            <div className="flex w-full items-center gap-3">
                                <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                <div className="flex flex-col gap-2">
                                    <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                    <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                </div>
                            </div>
                            <div className="flex w-full items-center gap-3">
                                <div className="h-8 w-8 animate-pulse overflow-hidden rounded-md border bg-slate-200 shadow-sm"></div>
                                <div className="flex flex-col gap-2">
                                    <div className="h-3 w-36 animate-pulse rounded bg-slate-200 "></div>
                                    <div className="h-3 w-24 animate-pulse rounded bg-slate-200 "></div>
                                </div>
                            </div>
                        </div>



                    </div>}
                </div>
            </Modal>
        </>
    );
}

// Update ResultRenderer to accept props
function ResultRenderer({ predictions, setShowCities, setCityError, setSelectedAirport, setCity, isSmall }: ResultRendererProps) {
    return <Accordion
        type="single"
        collapsible
        defaultValue={predictions?.[0]?.city}
    >
        {predictions?.map((city, index) => (
            <AccordionItem value={city.city} key={index}>
                <AccordionTrigger>
                    <div className='grid px-4 items-center grid-cols-[24px,_1fr] gap-2'>
                        <div className='flex items-center justify-center'>
                            <LocationIcon className='w-5' />
                        </div>
                        <div className='flex flex-col items-start'>
                            <span className='text-gray-800 font-medium text-sm'>
                                {city.city}, {city.country_name}
                            </span>
                            <span className='text-gray-400 text-sm font-normal'>All Airports</span>
                        </div>
                    </div>
                </AccordionTrigger>
                <AccordionContent className='pl-6'>
                    <div className='flex flex-col gap-1 max-h-52 overflow-y-auto'>
                        {city.airports.map(item => (
                            <div
                                key={item.airport_code}
                                onTouchStart={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setShowCities(false);
                                    setCityError(false);
                                    setSelectedAirport({
                                        airport_name: item.airport_name,
                                        airport_code: item.airport_code
                                    });
                                    setCity(item.airport_name);
                                }}
                                onClick={() => {
                                    if (isSmall) {
                                        return
                                    }
                                    setShowCities(false);
                                    setCityError(false);
                                    setSelectedAirport({
                                        airport_name: item.airport_name,
                                        airport_code: item.airport_code
                                    });
                                    setCity(item.airport_name);
                                }}
                                className="group grid cursor-pointer grid-cols-[30px,_1fr] place-items-start items-center gap-1 rounded-xl
                 px-1 py-2 transition-colors duration-200 hover:bg-gray-100 hover:text-emerald-700 lg:w-full"
                            >
                                <PlaneIcon className='rotate-[30deg]' />
                                <div className="flex flex-col gap-1">
                                    <p className="font-medium capitalize text-sm text-gray-800">
                                        {item.airport_name} ({item.airport_code})
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </AccordionContent>
            </AccordionItem>
        ))}
    </Accordion>
}